import { Component, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { loadApiSetting, ApiNotConfiguredNotice, fetchViaProxy } from "./apiSettingsHelpers";
import { loadFromSupabase } from "../supabaseDB";
import { computeLinearCorrection } from "./macroAdjustmentCalc";

/* GFI Nexus — Currency & Fuel (fix-dashboard.md "Fase C.2")
   ─────────────────────────────────────────────────────────────────
   fix-dashboard.md "Bug: ... crashea con Cannot read properties of
   undefined (reading 'toFixed')" — root cause: the FX provider and
   EIA can both return a shape with no usable rate/price (an error
   object instead of a rates/response.data field, a day missing the
   MXN symbol, etc.), which left `fx.latest` / `fuel.latest` as `null`
   while `fx`/`fuel` themselves were still truthy objects — the render
   only guarded "does fx exist", never "is fx.latest an actual number".
   Every number formatted below now goes through fmtNum(), and the
   whole module is wrapped in a local ErrorBoundary so ANY future
   uncaught render error shows a readable fallback instead of the
   silent gray-screen unmount every other module in this app is
   equally exposed to (none of them have an Error Boundary either —
   this is the first one in the codebase, scoped to this file only). */

/* typeof + isNaN guard — never throws, never renders "undefined". */
function fmtNum(v, decimals) {
  return (typeof v === "number" && !isNaN(v)) ? v.toFixed(decimals) : "—";
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error: error };
  }
  componentDidCatch(error, info) {
    console.error("[CurrencyFuelRegistry] render error:", error, info && info.componentStack);
  }
  render() {
    if (this.state.error) {
      var lang = this.props.lang;
      return (
        <div style={{ padding: "16px 20px", borderRadius: 10, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 12.5 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{lang === "es" ? "Ocurrió un error en este módulo" : "This module hit an error"}</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{this.state.error && this.state.error.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

var AUTO_REFRESH_MS = 60 * 60 * 1000;

var T = {
  en: {
    title: "Currency & Fuel", subtitle: "MXN/USD exchange rate and US diesel prices — logistics cost drivers.",
    refresh: "Refresh", refreshing: "Loading…", retry: "Retry", loadError: "Could not load data.",
    mxnTitle: "MXN/USD", mxnSub: "30-day history", vsYesterday: "vs. yesterday",
    fuelTitle: "US Diesel", fuelSub: "12-week history", vsLastWeek: "vs. last week",
    updated: "Updated", notConfiguredFx: "Frankfurter (Currency)", notConfiguredFuel: "US EIA",
    sensitivityTitle: "Fuel/MXN Sensitivity (Fase B — historical correlation)",
    sensitivityNote: "Real linear correlation (7-year weekly history) vs Blackberry/USDA price — the most complete real price series available. A pure statistical relationship, not a prediction: correlation strength varies, and other factors (season, weather, supply) usually dominate a single variable like this.",
    dieselLabel: "Diesel → price", mxnLabel: "MXN/USD → price",
    slopeDiesel: "For every $1/gal diesel moves, price has historically moved {slope}",
    slopeMxn: "For every $1 MXN/USD moves, price has historically moved {slope}",
    corrStrengthWeak: "weak correlation", corrStrengthModerate: "moderate correlation", corrStrengthStrong: "strong correlation",
    notEnoughCorr: "Not enough overlapping weekly history yet to compute this.",
    pointsUsedLabel: "weeks of overlapping history",
  },
  es: {
    title: "Divisa y Combustible", subtitle: "Tipo de cambio MXN/USD y precio de diésel en EE.UU. — factores de costo logístico.",
    refresh: "Actualizar", refreshing: "Cargando…", retry: "Reintentar", loadError: "No se pudo cargar la información.",
    mxnTitle: "MXN/USD", mxnSub: "Histórico 30 días", vsYesterday: "vs. ayer",
    fuelTitle: "Diésel EE.UU.", fuelSub: "Histórico 12 semanas", vsLastWeek: "vs. semana pasada",
    updated: "Actualizado", notConfiguredFx: "Frankfurter (Divisa)", notConfiguredFuel: "US EIA",
    sensitivityTitle: "Sensibilidad Combustible/MXN (Fase B — correlación histórica)",
    sensitivityNote: "Correlación lineal real (historial semanal de 7 años) vs precio Blackberry/USDA — la serie de precio real más completa disponible. Es una relación estadística pura, no una predicción: la fuerza de la correlación varía, y normalmente otros factores (temporada, clima, oferta) dominan sobre una sola variable como esta.",
    dieselLabel: "Diésel → precio", mxnLabel: "MXN/USD → precio",
    slopeDiesel: "Por cada $1/gal que se mueve el diésel, el precio históricamente se ha movido {slope}",
    slopeMxn: "Por cada $1 que se mueve MXN/USD, el precio históricamente se ha movido {slope}",
    corrStrengthWeak: "correlación débil", corrStrengthModerate: "correlación moderada", corrStrengthStrong: "correlación fuerte",
    notEnoughCorr: "Todavía no hay suficiente historial semanal solapado para calcular esto.",
    pointsUsedLabel: "semanas de historial solapado",
  },
};

function corrStrengthLabel(t, r) {
  var abs = Math.abs(r || 0);
  if (abs >= 0.5) return t.corrStrengthStrong;
  if (abs >= 0.25) return t.corrStrengthModerate;
  return t.corrStrengthWeak;
}

function CorrelationRow({ label, result, slopeTemplate, t }) {
  if (!result || !result.enough) {
    return (
      <div style={{ padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.notEnoughCorr}</div>
      </div>
    );
  }
  var slopeStr = (result.slope > 0 ? "+$" : "-$") + Math.abs(result.slope).toFixed(2);
  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid var(--color-border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
        <span style={{ fontSize: 12, fontWeight: 700 }}>{label}</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: Math.abs(result.correlation) >= 0.25 ? "var(--color-accent)" : "var(--color-text-muted)" }}>
          r = {result.correlation} ({corrStrengthLabel(t, result.correlation)})
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{slopeTemplate.replace("{slope}", slopeStr)}</div>
      <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", marginTop: 2 }}>{result.pointsUsed} {t.pointsUsedLabel}</div>
    </div>
  );
}

function fmtDateShort(iso, lang) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "short", day: "2-digit" }); }
  catch (e) { return iso; }
}

function ChangeBadge({ pct, label }) {
  if (typeof pct !== "number" || isNaN(pct)) return null;
  var up = pct >= 0;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: up ? "#059669" : "#DC2626" }}>
      {up ? "▲" : "▼"} {fmtNum(Math.abs(pct), 2)}% <span style={{ fontWeight: 400, color: "var(--color-text-muted)" }}>{label}</span>
    </span>
  );
}

function ChartCard({ title, sub, loading, error, onRetry, retryLabel, loadingLabel, errorLabel, current, unit, changePct, changeLabel, series, updatedAt, updatedLabel }) {
  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>
      <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 12 }}>{sub}</div>

      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--color-text-muted)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="9" fill="none" stroke="var(--color-border)" strokeWidth="3" />
            <circle cx="12" cy="12" r="9" fill="none" stroke="var(--color-accent)" strokeWidth="3" strokeDasharray="14 42" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
            </circle>
          </svg>
          {loadingLabel}
        </div>
      )}
      {!loading && error && (
        <div>
          <div style={{ fontSize: 12, color: "#DC2626", marginBottom: 8 }}>{errorLabel}</div>
          <button type="button" onClick={onRetry} style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{retryLabel}</button>
        </div>
      )}
      {!loading && !error && series && series.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 26, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{current}{unit}</span>
            <ChangeBadge pct={changePct} label={changeLabel} />
          </div>
          <div style={{ width: "100%", height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9 }} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="value" stroke="#2A7A6F" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 6 }}>{updatedLabel}: {updatedAt}</div>
        </>
      )}
    </div>
  );
}

export default function CurrencyFuelRegistry({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];

  var [fxCfg, setFxCfg] = useState(null);
  var [eiaCfg, setEiaCfg] = useState(null);
  var [loadingCfg, setLoadingCfg] = useState(true);

  var [fx, setFx] = useState(null);
  var [fxLoading, setFxLoading] = useState(false);
  var [fxError, setFxError] = useState(false);

  /* 2026-09-15 — Fase B "Fuel/MXN correcciones lineales" (CLAUDE.md
     roadmap). Historical macro_indicators + historical_prices (both now
     7-year backfilled) let this compute a REAL linear relationship
     (ordinary least squares + Pearson r) between each macro series and
     Blackberry/USDA price — the most complete real price series
     available — instead of guessing. Loaded once on mount; this is
     historical context, not something that needs the 60-minute live
     refresh the FX/fuel cards above use. */
  var [macroRows, setMacroRows] = useState([]);
  var [priceRowsForCorr, setPriceRowsForCorr] = useState([]);
  useEffect(function () {
    loadFromSupabase("macro_indicators").then(function (rows) { setMacroRows(rows || []); }).catch(function () { setMacroRows([]); });
    loadFromSupabase("historical_prices").then(function (rows) {
      setPriceRowsForCorr((rows || []).filter(function (r) { return r.source === "USDA" && r.product === "Blackberry"; }));
    }).catch(function () { setPriceRowsForCorr([]); });
  }, []);
  var dieselCorrelation = useMemo(function () {
    return computeLinearCorrection(macroRows.filter(function (r) { return r.metric === "DIESEL_USD_GAL"; }), priceRowsForCorr);
  }, [macroRows, priceRowsForCorr]);
  var mxnCorrelation = useMemo(function () {
    return computeLinearCorrection(macroRows.filter(function (r) { return r.metric === "MXN_USD"; }), priceRowsForCorr);
  }, [macroRows, priceRowsForCorr]);

  var [fuel, setFuel] = useState(null);
  var [fuelLoading, setFuelLoading] = useState(false);
  var [fuelError, setFuelError] = useState(false);

  var aliveRef = useRef(true);

  useEffect(function () {
    aliveRef.current = true;
    Promise.all([loadApiSetting("exchangerate_host"), loadApiSetting("us_eia")]).then(function (arr) {
      if (!aliveRef.current) return;
      setFxCfg(arr[0]);
      setEiaCfg(arr[1]);
      setLoadingCfg(false);
    });
    return function () { aliveRef.current = false; };
  }, []);

  var fetchFx = useCallback(async function (cfg) {
    setFxLoading(true); setFxError(false);
    try {
      var end = new Date();
      var start = new Date(end.getTime() - 29 * 86400000);
      var iso = function (d) { return d.toISOString().substring(0, 10); };
      /* Frankfurter (European Central Bank data, no key/registration —
         fix-dashboard.md, replaces exchangerate.host). Routed through
         the Netlify proxy — fix-dashboard.md "Bug CORS al llamar
         Frankfurter": it turned out NOT to send CORS headers for a
         direct browser fetch, despite being assumed CORS-open when
         this module was first built.
           - /latest?from=USD&to=MXN            → { rates: { MXN } }
           - /{start}..{end}?from=USD&to=MXN     → { rates: { "YYYY-MM-DD": { MXN } } }
         Fetched in parallel: /latest for the freshest headline number,
         the date-range endpoint purely for the 30-day chart. */
      var latestPath = "/latest?from=USD&to=MXN";
      var seriesPath = "/" + iso(start) + ".." + iso(end) + "?from=USD&to=MXN";
      var results = await Promise.allSettled([
        fetchViaProxy("exchangerate_host", latestPath, { endpointUrl: cfg.endpointUrl }),
        fetchViaProxy("exchangerate_host", seriesPath, { endpointUrl: cfg.endpointUrl }),
      ]);
      var latestOutcome = results[0], seriesOutcome = results[1];
      if (seriesOutcome.status !== "fulfilled") throw (seriesOutcome.reason || new Error("Frankfurter timeseries request failed"));
      var seriesData = seriesOutcome.value;
      var rates = seriesData.rates || {};
      var days = Object.keys(rates).sort();
      /* Filter out any day missing the MXN symbol instead of pushing
         `undefined` into the series — a single gappy day used to
         propagate all the way to fx.latest.toFixed() and crash. */
      var series = days
        .map(function (d) { return { label: fmtDateShort(d, lang), value: rates[d] && typeof rates[d].MXN === "number" ? rates[d].MXN : null }; })
        .filter(function (pt) { return pt.value !== null; });
      if (series.length === 0) throw new Error("No usable MXN rate in response");
      /* Headline number: prefer /latest (freshest); fall back to the
         series' own last day if /latest failed or came back malformed. */
      var latestData = latestOutcome.status === "fulfilled" ? latestOutcome.value : null;
      var latest = (latestData && latestData.rates && typeof latestData.rates.MXN === "number") ? latestData.rates.MXN : series[series.length - 1].value;
      var prev = series.length > 1 ? series[series.length - 2].value : latest;
      if (!aliveRef.current) return;
      setFx({ latest: latest, changePct: (typeof prev === "number" && prev) ? ((latest - prev) / prev) * 100 : 0, series: series, updatedAt: new Date().toLocaleTimeString(lang === "es" ? "es-MX" : "en-US", { hour: "2-digit", minute: "2-digit" }) });
    } catch (e) {
      if (aliveRef.current) setFxError(true);
    } finally {
      if (aliveRef.current) setFxLoading(false);
    }
  }, [lang]);

  var fetchFuel = useCallback(async function (cfg) {
    setFuelLoading(true); setFuelError(false);
    try {
      /* Routed through the Netlify proxy — same CORS-safety pass as
         Frankfurter above (fix-dashboard.md). The proxy appends
         &api_key=... itself (GENERIC_FETCH_APIS' keyParam), so this
         path omits it. */
      var path = "/petroleum/pri/gnd/data/?frequency=weekly&data[0]=value&facets[duoarea][]=NUS&facets[product][]=EPD2D"
        + "&sort[0][column]=period&sort[0][direction]=desc&offset=0&length=12";
      var data = await fetchViaProxy("us_eia", path, { apiKey: cfg.apiKey, endpointUrl: cfg.endpointUrl });
      /* EIA returns { error: "..." } (no `response`) on an invalid key
         or malformed request — surface that as a hard failure rather
         than falling through to an empty series. */
      if (data && data.error) throw new Error(typeof data.error === "string" ? data.error : "EIA request failed");
      var rows = (data.response && data.response.data) || [];
      var sorted = rows.slice().sort(function (a, b) { return a.period < b.period ? -1 : 1; });
      /* Filter out rows whose value doesn't parse to a real number
         instead of pushing NaN into the series. */
      var series = sorted
        .map(function (r) { var v = parseFloat(r.value); return { label: fmtDateShort(r.period, lang), value: isNaN(v) ? null : v }; })
        .filter(function (pt) { return pt.value !== null; });
      if (series.length === 0) throw new Error("No usable diesel price in response");
      var latest = series[series.length - 1].value;
      var prev = series.length > 1 ? series[series.length - 2].value : latest;
      if (!aliveRef.current) return;
      setFuel({ latest: latest, changePct: (typeof prev === "number" && prev) ? ((latest - prev) / prev) * 100 : 0, series: series, updatedAt: new Date().toLocaleTimeString(lang === "es" ? "es-MX" : "en-US", { hour: "2-digit", minute: "2-digit" }) });
    } catch (e) {
      if (aliveRef.current) setFuelError(true);
    } finally {
      if (aliveRef.current) setFuelLoading(false);
    }
  }, [lang]);

  useEffect(function () {
    if (!fxCfg || !fxCfg.configured) return;
    fetchFx(fxCfg);
    var id = setInterval(function () { fetchFx(fxCfg); }, AUTO_REFRESH_MS);
    return function () { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fxCfg]);

  useEffect(function () {
    if (!eiaCfg || !eiaCfg.configured) return;
    fetchFuel(eiaCfg);
    var id = setInterval(function () { fetchFuel(eiaCfg); }, AUTO_REFRESH_MS);
    return function () { clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eiaCfg]);

  if (loadingCfg) return null;

  return (
    <ErrorBoundary lang={lang}>
      <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
        <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "0 0 14px" }}>{t.subtitle}</p>

        {fxCfg && fxCfg.configured ? (
          <ChartCard
            title={t.mxnTitle} sub={t.mxnSub}
            loading={fxLoading && !fx} error={fxError} onRetry={function () { fetchFx(fxCfg); }}
            retryLabel={t.retry} loadingLabel={t.refreshing} errorLabel={t.loadError}
            current={fx ? fmtNum(fx.latest, 2) : "—"} unit="" changePct={fx ? fx.changePct : undefined} changeLabel={t.vsYesterday}
            series={fx ? fx.series : []} updatedAt={fx ? fx.updatedAt : ""} updatedLabel={t.updated}
          />
        ) : (
          <div style={{ marginBottom: 14 }}><ApiNotConfiguredNotice apiDisplayName={t.notConfiguredFx} lang={lang} /></div>
        )}

        {eiaCfg && eiaCfg.configured ? (
          <ChartCard
            title={t.fuelTitle} sub={t.fuelSub}
            loading={fuelLoading && !fuel} error={fuelError} onRetry={function () { fetchFuel(eiaCfg); }}
            retryLabel={t.retry} loadingLabel={t.refreshing} errorLabel={t.loadError}
            current={fuel ? fmtNum(fuel.latest, 3) : "—"} unit=" $/gal" changePct={fuel ? fuel.changePct : undefined} changeLabel={t.vsLastWeek}
            series={fuel ? fuel.series : []} updatedAt={fuel ? fuel.updatedAt : ""} updatedLabel={t.updated}
          />
        ) : (
          <ApiNotConfiguredNotice apiDisplayName={t.notConfiguredFuel} lang={lang} />
        )}

        <div style={{ marginTop: 4 }}>
          <button
            type="button"
            onClick={function () { if (fxCfg && fxCfg.configured) fetchFx(fxCfg); if (eiaCfg && eiaCfg.configured) fetchFuel(eiaCfg); }}
            style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >
            {t.refresh}
          </button>
        </div>

        <div style={{ marginTop: 18, padding: "14px 16px", borderRadius: 10, background: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{t.sensitivityTitle}</div>
          <div style={{ fontSize: 10.5, color: "var(--color-text-muted)", marginBottom: 10 }}>{t.sensitivityNote}</div>
          <CorrelationRow label={t.dieselLabel} result={dieselCorrelation} slopeTemplate={t.slopeDiesel} t={t} />
          <CorrelationRow label={t.mxnLabel} result={mxnCorrelation} slopeTemplate={t.slopeMxn} t={t} />
        </div>
      </div>
    </ErrorBoundary>
  );
}
